import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import moment from "moment";
import { AppDispatch } from "../../redux/store";
import { applyBundleSelection, fetchActivePlanV2, setMigrationFlowOpen } from "../../redux/slices/activePlanSlice";
import { fetchChangeSubscription } from "../../redux/slices/changeSubscriptionSlice";
import { showNotification } from "../../redux/slices/notificationSlice";
import useEnhancedNavigate from "../Session/helpers";
import DeleteModal from "../Shared/DeleteModal";
import {
  BASE_PROMPTS_PER_DAY,
  BUNDLE_CATALOGUE,
  CatalogueBundle,
  bundlePrice,
  catalogueFromApi,
  formatUsd,
} from "./bundleCatalogue";

const BundleSettings = ({ onClose }: { onClose: () => void }) => {
  const navigate = useEnhancedNavigate();
  const dispatch: AppDispatch = useDispatch();
  const apiPlans = useSelector((state: { plans }) => state.plans.plans);
  const plan = useSelector((state: { plan }) => state.plan.plan);
  const [pendingRemove, setPendingRemove] = useState<CatalogueBundle | null>(null);
  const [removing, setRemoving] = useState(false);

  const catalogue = useMemo(
    () => catalogueFromApi(apiPlans)
      ?? { bundles: BUNDLE_CATALOGUE, basePrompts: BASE_PROMPTS_PER_DAY },
    [apiPlans],
  );

  const ownedIds: string[] = useMemo(() => plan?.ownedBundles ?? [], [plan]);
  const ownedBundles = useMemo(
    () =>
      ownedIds.map(id =>
        catalogue.bundles.find(b => b.id === id)
        ?? { id, name: id, tagline: "", priceMonthly: null, features: [] } as CatalogueBundle,
      ),
    [ownedIds, catalogue],
  );

  const monthlyTotal = plan?.monthlySpend
    ?? ownedBundles.reduce((sum, b) => sum + (b.priceMonthly ?? 0), 0);
  const dailyPrompts = plan?.dailyPrompts
    ?? catalogue.basePrompts
    + ownedBundles.reduce((sum, b) => sum + (b.promptsPerDay ?? b.priceMonthly ?? 0), 0);
  const earnedPrompts = Math.max(0, dailyPrompts - catalogue.basePrompts);

  const expiresAt = plan?.subscription?.expires_at;
  const renewalLabel = plan?.non_renewing ? "Expires" : "Renews";

  const billingPeriod = plan?.billingPeriod ?? plan?.subscription?.billing_period ?? "month";
  const annualTotal = Math.round(
    ownedBundles.reduce((sum, b) => sum + (bundlePrice(b, true) ?? 0), 0) * 100,
  ) / 100;

  const isManualGrant = plan?.subscription?.provider === "manual";
  const deferredToRenewal = !isManualGrant;

  // TradeAlgo-comped plans aren't billed through TradeGPT — hide billing actions.
  const isTradeAlgoComped = plan?.comped === true
    && (plan?.subscription_provider ?? plan?.subscription?.provider) === "trade_algo";

  // Legacy Pro/Premium: a fixed tier, not per-bundle billing — monthlySpend is
  // meaningless ($0) and bundles can't be removed one-by-one (the backend
  // rejects it as "not a downgrade"). Changes go through the migration flow.
  const legacyPlan = plan?.legacyPlan;
  const perBundleBilling = !isTradeAlgoComped && !legacyPlan;

  const ownsAllBundles = catalogue.bundles
    .filter(b => b.priceMonthly != null)
    .every(b => ownedIds.includes(b.id));
  const renewalDate = expiresAt ? moment(expiresAt).format("DD MMM YYYY") : null;
  const effectiveClause = renewalDate ? `on ${renewalDate}` : "at your next renewal";

  // non_renewing = whole plan ending at expiry (scheduledChanges reads null then).
  const planEnding = !!plan?.non_renewing;

  const scheduled = plan?.scheduledChanges;
  const endingIds: string[] = planEnding
    ? ownedIds
    : scheduled
      ? ownedIds.filter(id => !scheduled.bundles.includes(id))
      : [];
  const scheduledEndDate = planEnding
    ? renewalDate
    : scheduled
      ? moment(scheduled.effective_at).format("DD MMM YYYY")
      : null;

  // Removal runs off the next-term baseline — re-posting an ending bundle would reinstate it.
  const nextTermIds = scheduled
    ? ownedIds.filter(id => scheduled.bundles.includes(id))
    : ownedIds;
  const nextTermBundles = ownedBundles.filter(b => nextTermIds.includes(b.id));
  const desiredAfterRemove = pendingRemove
    ? nextTermIds.filter(id => id !== pendingRemove.id)
    : nextTermIds;
  const removalCancelsPlan = !!pendingRemove && desiredAfterRemove.length === 0;

  const goToPlans = () => {
    onClose();
    navigate("/plans");
  };

  // Legacy plans change via the migration flow (MigrationModal in TradeGPT)
  const openMigrationFlow = () => {
    onClose();
    dispatch(setMigrationFlowOpen(true));
  };

  const handleRemove = async () => {
    if (!pendingRemove) return;
    setRemoving(true);
    try {
      if (removalCancelsPlan) {
        await dispatch(fetchChangeSubscription({ cancel: true })).unwrap();
      }
      else {
        await dispatch(
          applyBundleSelection({ bundleIds: desiredAfterRemove }),
        ).unwrap();
      }
      // Reconcile with the server-recomputed plan (entitlements, promptsLeft)
      dispatch(fetchActivePlanV2());
      dispatch(showNotification({
        message: removalCancelsPlan
          ? `${pendingRemove.name} will be removed ${effectiveClause} — your plan then drops to Free.`
          : deferredToRenewal
            ? `${pendingRemove.name} will be removed ${effectiveClause}.`
            : `${pendingRemove.name} removed from your plan.`,
        severity: "success",
        horizontal: "right",
      }));
    }
    catch {
      dispatch(showNotification({ message: "Couldn't remove the bundle, please try again.", severity: "error", horizontal: "right" }));
    }
    finally {
      setRemoving(false);
      setPendingRemove(null);
    }
  };

  // Post-removal rollup for the confirm copy, from the next-term baseline
  const survivingBundles = pendingRemove
    ? nextTermBundles.filter(b => b.id !== pendingRemove.id)
    : nextTermBundles;
  const promptsAfterRemove = catalogue.basePrompts
    + survivingBundles.reduce((sum, b) => sum + (b.promptsPerDay ?? b.priceMonthly ?? 0), 0);
  const totalAfterRemove = survivingBundles.reduce((sum, b) => sum + (b.priceMonthly ?? 0), 0);
  const removeSubText = removalCancelsPlan
    ? `This is your last active bundle${endingIds.length > 0 ? " (your other bundles are already scheduled to end)" : ""}, so removing it cancels your plan${deferredToRenewal ? ` ${effectiveClause}. You'll keep everything until then; after that` : " —"} your plan drops to Free: ${catalogue.basePrompts} prompts/day, basic tools only, $0/mo.${deferredToRenewal ? " No refund for the current period." : ""}`
    : deferredToRenewal
      ? `You'll keep its tools and prompts until ${renewalDate ?? "your next renewal"}. After that, its tools lock, your prompts drop from ${dailyPrompts} to ${promptsAfterRemove}/day, and your bill drops from ${formatUsd(monthlyTotal)} to ${formatUsd(totalAfterRemove)}/mo. No refund for the current period — you keep what you paid for.`
      : `Removal applies immediately — its tools lock, your prompts drop from ${dailyPrompts} to ${promptsAfterRemove}/day, and your monthly total goes down from ${formatUsd(monthlyTotal)} to ${formatUsd(totalAfterRemove)}/mo.`;

  const bundleCaption = (bundle: CatalogueBundle) => {
    const parts = [];
    if (bundle.features.length > 0) parts.push(`${bundle.features.length} tools`);
    if (bundle.priceMonthly != null) parts.push(`${formatUsd(bundle.priceMonthly)}/mo`);
    return parts.join(" · ");
  };

  return (
    <div className="w-full max-w-lg text-mui-black-87 dark:text-white">
      <Typography variant="h6/medium-500" component="h6" className="!mb-1">
        Your bundles
      </Typography>
      <Typography variant="caption" component="p" className="text-mui-black-60 dark:text-mui-white-56 !mb-4">
        {dailyPrompts}
        {" "}
        prompts/day
        {/* comped and legacy-tier allowances aren't earned via the $1/mo
            formula — the base + earned breakdown would just be confusing */}
        {perBundleBilling && (
          <>
            {" "}
            (
            {catalogue.basePrompts}
            {" "}
            base +
            {" "}
            {earnedPrompts}
            {" "}
            from bundles)
          </>
        )}
      </Typography>

      {ownedBundles.length === 0
        ? (
            <div className="rounded-lg border border-gray-200 dark:border-gray-700 py-8 px-4 text-center">
              <Typography variant="body2/regular-400" component="p" className="text-mui-black-60 dark:text-mui-white-56 !mb-3">
                No bundles yet. Add one to unlock tools and earn more prompts.
              </Typography>
              <Button
                variant="contained"
                onClick={goToPlans}
                className="!bg-black dark:!bg-white !text-white dark:!text-mui-black-87 !normal-case !py-1.5 !px-6"
              >
                Browse bundles
              </Button>
            </div>
          )
        : (
            <div className="flex flex-col">
              {ownedBundles.map(bundle => (
                <div
                  key={bundle.id}
                  className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-800 last:border-b-0"
                >
                  <div className="flex-1 min-w-0">
                    <Typography variant="body2/regular-400" className="!font-medium">
                      {bundle.name}
                    </Typography>
                    <Typography variant="caption" component="p" className="text-mui-black-60 dark:text-mui-white-56">
                      {bundleCaption(bundle)}
                    </Typography>
                    {endingIds.includes(bundle.id) && (
                      <Typography variant="caption" component="p" className="text-warning-main">
                        Scheduled to end on
                        {" "}
                        {scheduledEndDate}
                      </Typography>
                    )}
                  </div>
                  {perBundleBilling && (
                    <IconButton
                      size="small"
                      aria-label={`Remove ${bundle.name}`}
                      disabled={removing || endingIds.includes(bundle.id)}
                      onClick={() => setPendingRemove(bundle)}
                      className="!text-mui-black-38 dark:!text-mui-white-38 hover:!text-red-500 dark:hover:!text-red-400"
                    >
                      <DeleteOutlineRoundedIcon className="!w-[18px] !h-[18px]" />
                    </IconButton>
                  )}
                </div>
              ))}
            </div>
          )}

      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div>
          {isTradeAlgoComped
            ? (
                <>
                  <Typography variant="body2/regular-400" className="!font-medium">
                    Included with TradeAlgo
                  </Typography>
                  <Typography variant="caption" component="p" className="text-mui-black-60 dark:text-mui-white-56">
                    Your bundles are linked to your active TradeAlgo subscription.
                  </Typography>
                </>
              )
            : legacyPlan
              ? (
                  <>
                    <Typography variant="body2/regular-400" className="!font-medium">
                      {formatUsd(legacyPlan.price_monthly)}
                      /mo
                    </Typography>
                    <Typography variant="caption" component="p" className="text-mui-black-60 dark:text-mui-white-56">
                      TradeGPT
                      {" "}
                      {legacyPlan.type}
                      {" "}
                      — legacy plan
                    </Typography>
                    <Typography variant="caption" component="p" className="text-warning-main">
                      Moving to bundles — choose yours by
                      {" "}
                      {moment(legacyPlan.migrate_by).format("DD MMM YYYY")}
                    </Typography>
                  </>
                )
              : (
                  <>
                    <Typography variant="body2/regular-400" className="!font-medium">
                      {formatUsd(monthlyTotal)}
                      /mo
                    </Typography>
                    {billingPeriod === "year" && (
                      <Typography variant="caption" component="p" className="text-mui-black-60 dark:text-mui-white-56">
                        Billed annually (
                        {formatUsd(annualTotal)}
                        /yr)
                      </Typography>
                    )}
                    {expiresAt && (
                      <Typography variant="caption" component="p" className="text-mui-black-60 dark:text-mui-white-56">
                        {renewalLabel}
                        {" "}
                        {moment(expiresAt).format("DD MMM YYYY")}
                      </Typography>
                    )}
                  </>
                )}
        </div>
        {legacyPlan
          ? (
              <Button
                variant="outlined"
                onClick={openMigrationFlow}
                className="!normal-case !border-tgpt-secondary-states-outlinedBorder !text-tgpt-secondary-main hover:!bg-tgpt-secondary-states-hover"
              >
                Choose bundles
              </Button>
            )
          : !ownsAllBundles && (
              <Button
                variant="outlined"
                startIcon={<AddRoundedIcon />}
                onClick={goToPlans}
                className="!normal-case !border-tgpt-secondary-states-outlinedBorder !text-tgpt-secondary-main hover:!bg-tgpt-secondary-states-hover"
              >
                Add bundles
              </Button>
            )}
      </div>

      <DeleteModal
        open={!!pendingRemove}
        isDeleting={removing}
        handleDelete={handleRemove}
        handleCloseWarning={() => setPendingRemove(null)}
        mainText={`Remove ${pendingRemove?.name ?? "bundle"}?`}
        subText={removeSubText}
        confirmText="Remove bundle"
      />
    </div>
  );
};

export default BundleSettings;
